import { trpc } from '@/lib/trpc';

/**
 * BLU-550 / Phase 58-05 — client-side helpers for the manual "Reconcile &
 * force-push now" surface.
 *
 * Server-state ONLY: the run lives in `shopify_reconcile_runs` and we poll it.
 * There is deliberately no local `isRunning` mirror — navigate away and back
 * and the panel resumes from the row (per DESIGN.md honest-async rules).
 */

/** Terminal + in-flight statuses the backend can emit (mirrors ReconcileRunStatus). */
export type ReconcileRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'aborted_safeguard';

export const ACTIVE_RECONCILE_STATUSES: readonly ReconcileRunStatus[] = ['queued', 'running'];

export function isActiveStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'running';
}

/**
 * The amber circuit-breaker threshold the backend uses
 * (`VARIANT_LINKS_MAX_ARCHIVE` / `DEFAULT_ARCHIVE_SAFETY_CAP`). When a run's
 * archived count crosses this, render it amber so the safeguard abort reads as
 * the continuation of a visible trend rather than a surprise.
 */
export const VARIANT_LINKS_ARCHIVE_THRESHOLD = 5000;

/** Narrowed view of `shopify_reconcile_runs.scope` (jsonb on the wire). */
export type ReconcileScopeShape = { skuPrefix?: string; skuIds?: string[] } | null;

/**
 * Minimal wire-row shape for a reconcile run. The backend types `scope`/`stats`
 * as the recursive `Json` type; consuming them as that type triggers TS2589
 * ("excessively deep") at the property-access site. Casting the query result to
 * this shape (with `scope`/`stats` as `unknown`) severs that chain — the
 * `asReconcile*` coercers narrow them safely at runtime.
 */
export type WireReconcileRun = {
  id: string;
  status: ReconcileRunStatus;
  triggered_by: string;
  scope: unknown;
  stats: unknown;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

/** The reconcile run row as the UI consumes it (scope/stats narrowed from Json). */
export interface ReconcileRun {
  id: string;
  status: ReconcileRunStatus;
  triggered_by: string;
  scope: ReconcileScopeShape;
  stats: ReconcileStats;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/**
 * BLU-715b: one row of `stats.byLocation` — the per-location breakdown the run
 * recorder writes alongside the flat scalars (`cycleOutcomeToRun`).
 *
 * `phantomCandidates` is the count of Shopify levels at that location holding
 * stock the ERP has no record of: the class the phantom-zeroing pass considers,
 * which is why it is the number an operator needs BEFORE arming that location's
 * `erp_authoritative_for_stock` switch. It is an UPPER BOUND — it is counted in
 * `summarizeDriftPreview` from the raw drift rows, before `selectPhantomLevels`
 * applies the proven-writer / hybrid / exclusion vetoes.
 */
export interface ReconcileLocationStat {
  locationId: string;
  /** Server-resolved location name; falls back to the raw id. */
  location: string;
  phantomCandidates: number;
}

/** Known stat keys the backend writes (all optional — a queued run has none). */
export interface ReconcileStats {
  variantsChecked?: number;
  mismatches?: number;
  corrected?: number;
  correctionErrors?: number;
  linksUpserted?: number;
  linksArchived?: number;
  zeroed?: number;
  /** BLU-550: hybrid-location subset of `zeroed` (the zeroPhantomAtHybrid drain). */
  zeroedAtHybrid?: number;
  /**
   * BLU-715b: phantom candidates that passed every OTHER veto this run and were
   * held back purely because their location is not on the per-location
   * `erp_authoritative_for_stock` allow-list. Unlike `phantomCandidates` this
   * one IS veto-applied, so it is the exact number arming would have zeroed.
   */
  suppressedByNoAuthority?: number;
  /** BLU-720 / BLU-715b: per-location breakdown (jsonb array in the run row). */
  byLocation?: ReconcileLocationStat[];
  circuitBreaker?: boolean;
  linksEmpty?: boolean;
  targeted?: number;
  suppressedInsaneValues?: number;
  [key: string]: number | boolean | string | ReconcileLocationStat[] | undefined;
}

/** Safely coerce the wire `scope` (Json | null) into the narrowed shape. */
export function asReconcileScope(scope: unknown): ReconcileScopeShape {
  if (!scope || typeof scope !== 'object') return null;
  const s = scope as Record<string, unknown>;
  const out: { skuPrefix?: string; skuIds?: string[] } = {};
  if (typeof s.skuPrefix === 'string') out.skuPrefix = s.skuPrefix;
  if (Array.isArray(s.skuIds)) out.skuIds = s.skuIds.filter((x): x is string => typeof x === 'string');
  return out.skuPrefix || out.skuIds ? out : null;
}

/** Safely coerce the wire `stats` (Json) into a typed record. */
export function asReconcileStats(stats: unknown): ReconcileStats {
  if (!stats || typeof stats !== 'object') return {};
  return stats as ReconcileStats;
}

/** Read a numeric stat, tolerating undefined/non-number wire values. */
export function statNum(stats: ReconcileStats, key: string): number {
  const v = stats[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Narrow `stats.byLocation` from jsonb. Validated per row rather than cast:
 * the column is jsonb, so a partial or pre-BLU-715b row must degrade to an
 * empty list, never to `undefined.toLocaleString()` inside a table cell.
 * Rows are returned biggest-candidate-count first — the reading order for
 * "which floor is the risk?".
 */
export function asReconcileLocationStats(stats: ReconcileStats): ReconcileLocationStat[] {
  const raw: unknown = stats.byLocation;
  if (!Array.isArray(raw)) return [];
  return raw
    .flatMap((row): ReconcileLocationStat[] => {
      if (!row || typeof row !== 'object') return [];
      const r = row as Record<string, unknown>;
      const locationId = typeof r.locationId === 'string' ? r.locationId : '';
      if (!locationId) return [];
      const candidates = typeof r.phantomCandidates === 'number' && Number.isFinite(r.phantomCandidates)
        ? r.phantomCandidates
        : 0;
      return [
        {
          locationId,
          location: typeof r.location === 'string' && r.location.length > 0 ? r.location : locationId,
          phantomCandidates: candidates,
        },
      ];
    })
    .sort((a, b) => b.phantomCandidates - a.phantomCandidates);
}

/**
 * Org-wide sum of `byLocation[].phantomCandidates` — the upper bound on how
 * many Shopify levels the phantom-zeroing pass would set to 0 if every location
 * in the run were armed. Upper bound, not a forecast: see `ReconcileLocationStat`.
 */
export function phantomCandidateTotal(stats: ReconcileStats): number {
  return asReconcileLocationStats(stats).reduce((sum, l) => sum + l.phantomCandidates, 0);
}

/**
 * Recent reconcile runs for the run-history table.
 *
 * `select` re-types the result to `WireReconcileRun[]` (scope/stats as
 * `unknown`). The backend types `stats`/`scope` as the recursive `Json` type;
 * consuming it directly triggers TS2589 ("excessively deep") at the
 * property-access site, so we narrow it once here at the boundary.
 */
export function useReconcileRuns(limit = 20) {
  return trpc.stockSync.listReconcileRuns.useQuery(
    { limit },
    { select: (rows) => rows as unknown as WireReconcileRun[] },
  );
}

/**
 * Poll a single in-flight run. `refetchInterval` is gated on the run still
 * being active so a finished run stops polling. Pass `null` to disable.
 * `select` narrows to `WireReconcileRun` (see `useReconcileRuns`).
 */
export function useReconcileRun(runId: string | null) {
  return trpc.stockSync.getReconcileRun.useQuery(
    { id: runId ?? '' },
    {
      enabled: !!runId,
      // Param annotated `unknown` so TS never expands the recursive `Json`
      // result type (TS2589); the runtime coercers narrow it downstream.
      select: (row: unknown) => (row ?? null) as WireReconcileRun | null,
      refetchInterval: (query) => {
        const run = query.state.data as WireReconcileRun | null | undefined;
        return run && isActiveStatus(run.status) ? 2_000 : false;
      },
    },
  );
}
